export type CsvMatrix = string[][];

export function inferDelimiter(sampleLine: string): string {
  const candidates = [',', '\t', ';', '|'];
  let best = ',';
  let bestCount = -1;
  for (const c of candidates) {
    const count = sampleLine.split(c).length - 1;
    if (count > bestCount) {
      bestCount = count;
      best = c;
    }
  }
  return best;
}

export function parseCsv(text: string): { delimiter: string; rows: CsvMatrix } {
  const raw = (text ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const firstLine = raw.split('\n')[0] || '';
  const delimiter = inferDelimiter(firstLine);

  const rows: CsvMatrix = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  const pushCell = () => {
    row.push(cell);
    cell = '';
  };
  const pushRow = () => {
    // Avoid adding a trailing empty row if the file ends with newline
    if (row.length === 1 && row[0] === '' && rows.length > 0) {
      row = [];
      return;
    }
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (inQuotes) {
      if (ch === '"') {
        const next = raw[i + 1];
        if (next === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      pushCell();
      continue;
    }
    if (ch === '\n') {
      pushCell();
      pushRow();
      continue;
    }
    cell += ch;
  }
  pushCell();
  pushRow();

  const maxCols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  const normalized = rows.map((r) => (r.length < maxCols ? [...r, ...Array(maxCols - r.length).fill('')] : r));

  return { delimiter, rows: normalized };
}

export function csvEscape(value: string, delimiter: string): string {
  const v = value ?? '';
  const needsQuote = v.includes('"') || v.includes('\n') || v.includes('\r') || v.includes(delimiter);
  if (!needsQuote) return v;
  return `"${v.replace(/"/g, '""')}"`;
}

export function serializeCsv(rows: CsvMatrix, delimiter: string): string {
  return rows.map((r) => r.map((c) => csvEscape(`${c ?? ''}`, delimiter)).join(delimiter)).join('\n') + '\n';
}

export function normalizeCsvText(text: string): string {
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return normalized.replace(/\s*$/, '\n');
}

export function shouldUseRawCsvMode(rowsCount: number, colsCount: number): boolean {
  const cellCount = rowsCount * Math.max(1, colsCount);
  // We can safely render a lot more with a virtualized grid.
  // Keep a conservative upper bound to avoid pathological cases, but allow up to ~500 rows by default.
  return rowsCount > 500 || colsCount > 60 || cellCount > 30000;
}

